import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { Tag, deleteTag, searchTags } from '~/services/PublicOfferFeedService';

interface ManageTagsDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  campaignId: string;
  onTagsUpdated: () => void;
}

export default function ManageTagsDialog({
  open,
  onClose,
  customerId,
  campaignId,
  onTagsUpdated,
}: ManageTagsDialogProps) {
  const [tags, setTags] = useState<Array<Tag>>([]);
  const [loading, setLoading] = useState(false);
  const showConfirmDialog = useConfirmDialog();

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await searchTags(customerId, campaignId);
      setTags(response.result);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, campaignId]);

  const handleDeleteTag = async (tag: Tag) => {
    const confirmed = await showConfirmDialog({
      title: 'Delete Tag',
      description: `Are you sure you want to delete the tag "${tag.tagName}"? This will remove it from all ad groups in this campaign.`,
    });

    if (confirmed) {
      try {
        await deleteTag(tag.tagId.toString());
        await fetchTags();
        onTagsUpdated();
      } catch (error) {
        console.error('Error deleting tag:', error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Tags
        <Typography variant="body2" color="text.secondary">
          Tags for Campaign: {campaignId}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography>Loading tags...</Typography>
        ) : tags.length === 0 ? (
          <Typography color="text.secondary">No tags found for this campaign.</Typography>
        ) : (
          <List>
            {tags.map((tag) => (
              <ListItem
                key={tag.tagId}
                secondaryAction={
                  <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteTag(tag)}>
                    Delete
                  </Button>
                }
              >
                <ListItemText primary={tag.tagName} secondary={`Tag ID: ${tag.tagId}`} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
